import * as React from "react";
import { useMemo } from "react";

import "@fontsource/lato/300.css";
import "@fontsource/lato/400.css";
import "@fontsource/lato/700.css";
import "@fontsource/lato/300-italic.css";
import "@fontsource/lato/400-italic.css";
import "@fontsource/lato/700-italic.css";
import {
  CssBaseline,
  PaletteOptions,
  ThemeProvider,
  StyledEngineProvider,
  createTheme,
} from "@mui/material";
import { CacheProvider } from "@emotion/react";
import type {
  BrandingColorResponseName,
  BrandingColorResponse,
} from "generated/model";
import { defineMessage, MessageDescriptor } from "react-intl";
import { rgba } from "color2k";
import { rtlCache } from "./rtlCache";

export const FOUNDATIONS_PALETTE_PROPERTIES: Required<
  Pick<PaletteOptions, "contrastThreshold" | "tonalOffset">
> = {
  // When `contrastText` is omitted from a palette option, it will be calculated automatically
  // to contrast with `main`, according to the `contrastThreshold` value below
  contrastThreshold: 4.5, // 4.5 meets the minimum contrast of 4.5:1 as defined in WCAG 2.1
  // When `light` and `dark` are omitted from a palette option, they will be calculated
  // automatically from `main`, according to the `tonalOffset` value below
  tonalOffset: 0.2,
};

export interface BrandingColorDetails {
  hexColor: string;
  label: MessageDescriptor;
}

export const DEFAULT_BRANDING_COLORS = new Map<
  BrandingColorResponseName,
  BrandingColorDetails
>([
  [
    "LANGUAGE_ICON",
    {
      hexColor: "#453776",
      label: defineMessage({
        defaultMessage: "Language Icon",
        description: "Label associated with the LANGUAGE_ICON branding color",
      }),
    },
  ],
  [
    "LANGUAGE_ICON_HOVER",
    {
      hexColor: "#2A68DB",
      label: defineMessage({
        defaultMessage: "Language Icon (Hover)",
        description:
          "Label associated with the LANGUAGE_ICON_HOVER branding color",
      }),
    },
  ],
  [
    "LANGUAGE_LINK",
    {
      hexColor: "#444444",
      label: defineMessage({
        defaultMessage: "Language Link",
        description: "Label associated with the LANGUAGE_LINK branding color",
      }),
    },
  ],
  [
    "LANGUAGE_LINK_HOVER",
    {
      hexColor: "#444444",
      label: defineMessage({
        defaultMessage: "Language Link (Hover)",
        description:
          "Label associated with the LANGUAGE_LINK_HOVER branding color",
      }),
    },
  ],
  [
    "USER_MENU_NAME_TEXT",
    {
      hexColor: "#444444",
      label: defineMessage({
        defaultMessage: "User Menu Name",
        description:
          "Label associated with the USER_MENU_NAME_TEXT branding color",
      }),
    },
  ],
  [
    "USER_MENU_NAME_TEXT_HOVER",
    {
      hexColor: "#2A68DB",
      label: defineMessage({
        defaultMessage: "User Menu Name (Hover)",
        description:
          "Label associated with the USER_MENU_NAME_TEXT_HOVER branding color",
      }),
    },
  ],
  [
    "USER_MENU_LINK_ICONS",
    {
      hexColor: "#453776",
      label: defineMessage({
        defaultMessage: "User Menu Link Icons",
        description:
          "Label associated with the USER_MENU_LINK_ICONS branding color",
      }),
    },
  ],
  [
    "USER_MENU_LINK_ICONS_HOVER",
    {
      hexColor: "#2A68DB",
      label: defineMessage({
        defaultMessage: "User Menu Link Icons (Hover)",
        description:
          "Label associated with the USER_MENU_LINK_ICONS_HOVER branding color",
      }),
    },
  ],
  [
    "USER_MENU_LINK_TEXT",
    {
      hexColor: "#444444",
      label: defineMessage({
        defaultMessage: "User Menu Link Text",
        description:
          "Label associated with the USER_MENU_LINK_TEXT branding color",
      }),
    },
  ],
  [
    "USER_MENU_LINK_TEXT_HOVER",
    {
      hexColor: "#999999",
      label: defineMessage({
        defaultMessage: "User Menu Link Text (Hover)",
        description:
          "Label associated with the USER_MENU_LINK_TEXT_HOVER branding color",
      }),
    },
  ],
  [
    "USER_MENU_POPUP_BACKGROUND",
    {
      hexColor: "#FFFFFF",
      label: defineMessage({
        defaultMessage: "User Menu Popup Background",
        description:
          "Label associated with the USER_MENU_POPUP_BACKGROUND branding color",
      }),
    },
  ],
  [
    "ADMIN_PANEL_BACKGROUND",
    {
      hexColor: "#F7F7F7",
      label: defineMessage({
        defaultMessage: "Panel Background",
        description:
          "Label associated with the PANEL_BACKGROUND branding color",
      }),
    },
  ],
  [
    "ADMIN_PANEL_BORDERS",
    {
      hexColor: "#E1E1E1",
      label: defineMessage({
        defaultMessage: "Panel Borders",
        description: "Label associated with the PANEL_BORDERS branding color",
      }),
    },
  ],
  [
    "ADMIN_PANEL_TITLES",
    {
      hexColor: "#444444",
      label: defineMessage({
        defaultMessage: "Panel Titles",
        description: "Label associated with the PANEL_TITLES branding color",
      }),
    },
  ],
  [
    "ADMIN_PANEL_LINK_TEXT",
    {
      hexColor: "#444444",
      label: defineMessage({
        defaultMessage: "Panel Link Text",
        description: "Label associated with the PANEL_LINK_TEXT branding color",
      }),
    },
  ],
  [
    "ADMIN_PANEL_LINK_TEXT_HOVER",
    {
      hexColor: "#453776",
      label: defineMessage({
        defaultMessage: "Panel Link Text (Hover)",
        description:
          "Label associated with the PANEL_LINK_TEXT_HOVER branding color",
      }),
    },
  ],
  [
    "ADMIN_PANEL_ACTIVE_LINK_TEXT",
    {
      hexColor: "#453776",
      label: defineMessage({
        defaultMessage: "Active Link Text",
        description:
          "Label associated with the ACTIVE_LINK_TEXT branding color",
      }),
    },
  ],
  [
    "ADMIN_PANEL_ACTIVE_LINK_BACKGROUND",
    {
      hexColor: "#FFFFFF",
      label: defineMessage({
        defaultMessage: "Active Link Background",
        description:
          "Label associated with the ACTIVE_LINK_BACKGROUND branding color",
      }),
    },
  ],
  [
    "ADMIN_PANEL_TOGGLE_ICON_BACKGROUND",
    {
      hexColor: "#FFFFFF",
      label: defineMessage({
        defaultMessage: "Toggle Icon Background",
        description:
          "Label associated with the TOGGLE_ICON_BACKGROUND branding color",
      }),
    },
  ],
  [
    "ADMIN_PANEL_TOGGLE_ICON",
    {
      hexColor: "#2A68DB",
      label: defineMessage({
        defaultMessage: "Toggle Icon",
        description: "Label associated with the TOGGLE_ICON branding color",
      }),
    },
  ],
  [
    "ADMIN_PANEL_TOGGLE_ICON_BACKGROUND_HOVER",
    {
      hexColor: "#2A68DB",
      label: defineMessage({
        defaultMessage: "Toggle Icon Background (Hover)",
        description:
          "Label associated with the TOGGLE_ICON_BACKGROUND_HOVER branding color",
      }),
    },
  ],
  [
    "ADMIN_PANEL_TOGGLE_ICON_HOVER",
    {
      hexColor: "#FFFFFF",
      label: defineMessage({
        defaultMessage: "Toggle Icon (Hover)",
        description:
          "Label associated with the TOGGLE_ICON_HOVER branding color",
      }),
    },
  ],
  [
    "ADMIN_PANEL_ARROW_ICON",
    {
      hexColor: "#2A68DB",
      label: defineMessage({
        defaultMessage: "Arrow Icon",
        description: "Label associated with the ARROW_ICON branding color",
      }),
    },
  ],
  [
    "NAV_BACKGROUND",
    {
      hexColor: "#FFFFFF",
      label: defineMessage({
        defaultMessage: "Navigation Background",
        description: "Label associated with the NAV_BACKGROUND branding color",
      }),
    },
  ],
  [
    "NAV_BORDERS",
    {
      hexColor: "#E1E1E1",
      label: defineMessage({
        defaultMessage: "Navigation Borders",
        description: "Label associated with the NAV_BORDERS branding color",
      }),
    },
  ],
  [
    "NAV_LOGO_BOX_BACKGROUND",
    {
      hexColor: "#FFFFFF",
      label: defineMessage({
        defaultMessage: "Logo Box Background",
        description:
          "Label associated with the LOGO_BOX_BACKGROUND branding color",
      }),
    },
  ],
  [
    "NAV_LINK_TEXT",
    {
      hexColor: "#444444",
      label: defineMessage({
        defaultMessage: "Nav. Link Text",
        description: "Label associated with the NAV_LINK_TEXT branding color",
      }),
    },
  ],
  [
    "NAV_LINK_TEXT_HOVER",
    {
      hexColor: "#2A68DB",
      label: defineMessage({
        defaultMessage: "Nav. Link Text (Hover)",
        description:
          "Label associated with the NAV_LINK_TEXT_HOVER branding color",
      }),
    },
  ],
  [
    "NAV_MOBILE_ICON",
    {
      hexColor: "#453776",
      label: defineMessage({
        defaultMessage: "Mobile Nav. Menu Icon",
        description: "Label associated with the NAV_MOBILE_ICON branding color",
      }),
    },
  ],
  [
    "NAV_MOBILE_ICON_HOVER",
    {
      hexColor: "#2A68DB",
      label: defineMessage({
        defaultMessage: "Mobile Nav. Menu Icon (Hover)",
        description:
          "Label associated with the NAV_MOBILE_ICON_HOVER branding color",
      }),
    },
  ],
]);

/**
 * The foundations color palette. All components in the app should use these colors (through
 * the MUI theme) unless specified otherwise.
 */
export const initializePalette = (
  colors: BrandingColorResponse[],
): PaletteOptions => {
  return {
    mode: "light",
    primary: {
      dark: "#292048",
      main: "#453776",
      light: "#E3E0F1",
    },
    secondary: {
      main: "#7158D6",
      light: "#8541D9",
      dark: "#5A46AC",
    },
    white: {
      main: "#FFFFFF",
      dark: rgba(0, 0, 0, 0.1),
    },
    black: {
      main: "#000000",
    },
    stroke: {
      main: "#E1E1E1",
      dark: "#E8E8E8",
      light: "#D5D2E0",
      // Meets the 3:1 contrast ratio WCAG 1.4.11 requires for borders on interactive
      // controls (inputs, buttons, cards, modals).
      contrast: "#888888",
    },
    success: {
      main: "#10A34E",
      text: "#267648",
      light: "#F6FBF7",
    },
    warning: {
      main: "#FF9900",
      text: "#8A6D3B",
      light: "#FFFAF6",
    },
    error: {
      main: "#C9302C",
      light: "#FDF6F5",
      text: "#A94442",
    },
    text: {
      dark: "#272727",
      primary: "#444444",
      secondary: "#666666",
      // Should be used on large text only (isn't 4.5:1 on white bg)
      light: "#888888",
      disabled: "#CCCCCC",
    },
    background: {
      default: "#F7F7F7",
      paper: "#FFFFFF",
    },
    blue: {
      main: "#2A68DB",
      light: "#EBF1FB",
    },
    navbar: {
      background:
        colors.find((value) => value.name === "NAV_BACKGROUND")?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("NAV_BACKGROUND")?.hexColor,
      stroke:
        colors.find((value) => value.name === "NAV_BORDERS")?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("NAV_BORDERS")?.hexColor,
      text:
        colors.find((value) => value.name === "NAV_LINK_TEXT")?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("NAV_LINK_TEXT")?.hexColor,
      hover:
        colors.find((value) => value.name === "NAV_LINK_TEXT_HOVER")?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("NAV_LINK_TEXT_HOVER")?.hexColor,
    },
    navLogo: {
      background:
        colors.find((value) => value.name === "NAV_LOGO_BOX_BACKGROUND")
          ?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("NAV_LOGO_BOX_BACKGROUND")?.hexColor,
    },
    navMobileMenu: {
      icons:
        colors.find((value) => value.name === "NAV_MOBILE_ICON")?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("NAV_MOBILE_ICON")?.hexColor,
      iconsHover:
        colors.find((value) => value.name === "NAV_MOBILE_ICON_HOVER")
          ?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("NAV_MOBILE_ICON_HOVER")?.hexColor,
    },
    adminPanel: {
      background:
        colors.find((value) => value.name === "ADMIN_PANEL_BACKGROUND")
          ?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("ADMIN_PANEL_BACKGROUND")?.hexColor,
      stroke:
        colors.find((value) => value.name === "ADMIN_PANEL_BORDERS")?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("ADMIN_PANEL_BORDERS")?.hexColor,
      text:
        colors.find((value) => value.name === "ADMIN_PANEL_TITLES")?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("ADMIN_PANEL_TITLES")?.hexColor,
    },
    adminPanelLink: {
      background:
        colors.find(
          (value) => value.name === "ADMIN_PANEL_ACTIVE_LINK_BACKGROUND",
        )?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("ADMIN_PANEL_ACTIVE_LINK_BACKGROUND")
          ?.hexColor,
      text:
        colors.find((value) => value.name === "ADMIN_PANEL_LINK_TEXT")
          ?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("ADMIN_PANEL_LINK_TEXT")?.hexColor,
      hover:
        colors.find((value) => value.name === "ADMIN_PANEL_LINK_TEXT_HOVER")
          ?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("ADMIN_PANEL_LINK_TEXT_HOVER")?.hexColor,
      active:
        colors.find((value) => value.name === "ADMIN_PANEL_ACTIVE_LINK_TEXT")
          ?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("ADMIN_PANEL_ACTIVE_LINK_TEXT")?.hexColor,
    },
    adminPanelToggle: {
      background:
        colors.find(
          (value) => value.name === "ADMIN_PANEL_TOGGLE_ICON_BACKGROUND",
        )?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("ADMIN_PANEL_TOGGLE_ICON_BACKGROUND")
          ?.hexColor,
      backgroundHover:
        colors.find(
          (value) => value.name === "ADMIN_PANEL_TOGGLE_ICON_BACKGROUND_HOVER",
        )?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("ADMIN_PANEL_TOGGLE_ICON_BACKGROUND_HOVER")
          ?.hexColor,
      main:
        colors.find((value) => value.name === "ADMIN_PANEL_TOGGLE_ICON")
          ?.hexCode ??
        (DEFAULT_BRANDING_COLORS.get("ADMIN_PANEL_TOGGLE_ICON")
          ?.hexColor as string),
      hover:
        colors.find((value) => value.name === "ADMIN_PANEL_TOGGLE_ICON_HOVER")
          ?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("ADMIN_PANEL_TOGGLE_ICON_HOVER")?.hexColor,
    },
    adminPanelArrowIcon: {
      main:
        colors.find((value) => value.name === "ADMIN_PANEL_ARROW_ICON")
          ?.hexCode ??
        (DEFAULT_BRANDING_COLORS.get("ADMIN_PANEL_ARROW_ICON")
          ?.hexColor as string),
    },
    userMenuChip: {
      text:
        colors.find((value) => value.name === "USER_MENU_NAME_TEXT")?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("USER_MENU_NAME_TEXT")?.hexColor,
      hover:
        colors.find((value) => value.name === "USER_MENU_NAME_TEXT_HOVER")
          ?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("USER_MENU_NAME_TEXT_HOVER")?.hexColor,
    },
    userMenuPopup: {
      background:
        colors.find((value) => value.name === "USER_MENU_POPUP_BACKGROUND")
          ?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("USER_MENU_POPUP_BACKGROUND")?.hexColor,
      text:
        colors.find((value) => value.name === "USER_MENU_LINK_TEXT")?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("USER_MENU_LINK_TEXT")?.hexColor,
      hover:
        colors.find((value) => value.name === "USER_MENU_LINK_TEXT_HOVER")
          ?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("USER_MENU_LINK_TEXT_HOVER")?.hexColor,
      icons:
        colors.find((value) => value.name === "USER_MENU_LINK_ICONS")
          ?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("USER_MENU_LINK_ICONS")?.hexColor,
      iconsHover:
        colors.find((value) => value.name === "USER_MENU_LINK_ICONS_HOVER")
          ?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("USER_MENU_LINK_ICONS_HOVER")?.hexColor,
    },
    languageToggle: {
      text:
        colors.find((value) => value.name === "LANGUAGE_LINK")?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("LANGUAGE_LINK")?.hexColor,
      hover:
        colors.find((value) => value.name === "LANGUAGE_LINK_HOVER")?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("LANGUAGE_LINK_HOVER")?.hexColor,
      icons:
        colors.find((value) => value.name === "LANGUAGE_ICON")?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("LANGUAGE_ICON")?.hexColor,
      iconsHover:
        colors.find((value) => value.name === "LANGUAGE_ICON_HOVER")?.hexCode ??
        DEFAULT_BRANDING_COLORS.get("LANGUAGE_ICON_HOVER")?.hexColor,
    },
    ...FOUNDATIONS_PALETTE_PROPERTIES,
  };
};

interface FoundationThemeProviderProps {
  colors: BrandingColorResponse[];
  direction?: "ltr" | "rtl";
}

/**
 * The customized MUI {@link ThemeProvider} that should be wrapped around all foundations
 * components.
 */
export function FoundationsThemeProvider(
  props: Readonly<
    {
      children: React.ReactNode;
    } & FoundationThemeProviderProps
  >,
): React.ReactElement {
  const muiTheme = useMemo(
    () =>
      createTheme({
        direction: props.direction ?? "ltr",
        palette: initializePalette(props.colors),
        breakpoints: {
          values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1920,
          },
        },
        typography: {
          fontFamily: "'Lato', sans-serif",
          h1: {
            fontSize: "2.5rem",
            fontWeight: 700,
          },
          h2: {
            fontSize: "2rem",
            fontWeight: 700,
          },
          h3: {
            fontSize: "1.5rem",
            fontWeight: 700,
          },
          h4: {
            fontSize: "1.25rem",
            fontWeight: 700,
          },
          h5: {
            fontSize: "1.125rem",
            fontWeight: 700,
          },
          h6: {
            fontSize: "1rem",
            fontWeight: 700,
          },
          subtitle1: {
            fontSize: "0.875rem",
            fontWeight: 700,
            textTransform: "uppercase",
            color: "rgba(0, 0, 0, 0.66)",
          },
          subtitle2: {
            fontSize: "0.875rem",
            fontWeight: 700,
            color: "rgba(0, 0, 0, 0.66)",
          },
          button: {
            letterSpacing: "0.022em",
          },
        },
        spacing: 8,
        mixins: {
          toolbar: {
            minHeight: 60,
            maxHeight: 60,
          },
          fieldWidth: 280,
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              fontFamily: "'Lato', sans-serif",
              // a11y: focus ring for pickers. Class repeated and `!important` to
              // outrank the picker's own focused border.
              ".MuiPickersInputBase-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline.MuiPickersOutlinedInput-notchedOutline":
                {
                  borderWidth: 3,
                  borderColor: "#000000 !important",
                  "@media (forced-colors: active)": { borderWidth: 4 },
                },
              // a11y: forced colors flattens ring and border to one colour, so width
              // is all that marks focus. `!important` to outrank per-component rules.
              "@media (forced-colors: active)": {
                ":focus-visible": { outlineWidth: "4px !important" },
              },
            },
          },
          MuiUseMediaQuery: {
            defaultProps: {
              // Since we do not use server-side rendering, always set `noSsr` to true
              // https://mui.com/material-ui/react-use-media-query/#client-side-only-rendering
              noSsr: true,
            },
          },
          MuiStack: {
            defaultProps: {
              useFlexGap: true,
            },
          },
          MuiTypography: {
            defaultProps: {
              // Maps all relevant variants to paragraph tags instead of heading elements to
              // prevent incorrect heading structure when using a variant for visual purposes
              variantMapping: {
                h1: "p",
                h2: "p",
                h3: "p",
                h4: "p",
                h5: "p",
                h6: "p",
                subtitle1: "p",
                subtitle2: "p",
              },
            },
          },
          MuiAvatar: {
            styleOverrides: {
              colorDefault: {
                // Fix color contrast for the default background color on letter avatars
                backgroundColor: "#666666",
              },
            },
          },
          MuiCardActions: {
            styleOverrides: {
              root: {
                // Allow card action items to wrap if necessary for smaller screen size support
                flexWrap: "wrap",
              },
            },
          },
          MuiBadge: {
            styleOverrides: {
              invisible: {
                // Fix accessibility issue with badges when they're hidden
                visibility: "hidden",
                transform: "none !important",
              },
            },
          },
          MuiPaper: {
            defaultProps: {
              elevation: 0,
              variant: "outlined",
            },
          },
          MuiSwitch: {
            styleOverrides: {
              root: {
                // a11y: forced colors flattens the track and thumb fills to Canvas.
                "@media (forced-colors: active)": {
                  "& .MuiSwitch-track": {
                    backgroundColor: "Canvas",
                    border: "1px solid ButtonBorder",
                    opacity: 1,
                  },
                  "& .MuiSwitch-thumb": {
                    backgroundColor: "ButtonText",
                  },
                  "& .Mui-checked": {
                    "& .MuiSwitch-thumb": {
                      backgroundColor: "Highlight",
                    },
                    "& + .MuiSwitch-track": {
                      backgroundColor: "Canvas",
                      borderColor: "Highlight",
                    },
                  },
                  "& .Mui-disabled": {
                    "& .MuiSwitch-thumb": {
                      backgroundColor: "GrayText",
                    },
                    "& + .MuiSwitch-track": {
                      borderColor: "GrayText",
                    },
                  },
                },
              },
            },
          },
          MuiDataGrid: {
            styleOverrides: {
              root: {
                backgroundColor: "#ffffff !important",
              },
              cell: ({ theme }) => ({
                // The grid moves focus between cells with the arrow keys rather
                // than Tab, so it styles `:focus` instead of `:focus-visible`.
                "&:focus, &:focus-within": {
                  outline: `3px solid ${theme.palette.black.main}`,
                  // Inset because the cells sit in a clipped scroll container.
                  outlineOffset: -3,
                  "@media (forced-colors: active)": { outlineWidth: 4 },
                },
              }),
              columnHeader: ({ theme }) => ({
                backgroundColor: "#ffffff !important",
                "&:focus, &:focus-within": {
                  outline: `3px solid ${theme.palette.black.main}`,
                  outlineOffset: -3,
                  "@media (forced-colors: active)": { outlineWidth: 4 },
                },
              }),
            },
          },
          MuiFormControlLabel: {
            styleOverrides: {
              root: {
                "&:not(.MuiFormControlLabel-labelPlacementStart)": {
                  paddingLeft: 4,
                  "& .MuiFormControlLabel-label": {
                    marginLeft: 4,
                  },
                },
              },
              labelPlacementStart: {
                "& .MuiFormControlLabel-label": {
                  marginRight: 4,
                },
              },
            },
          },
          MuiMenuItem: {
            styleOverrides: {
              root: ({ theme }) => ({
                "&.Mui-focusVisible": {
                  outline: "3px solid",
                  outlineColor: theme.palette.black.main,
                  outlineOffset: "-2px",
                },
                "&.Mui-selected": {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.white.main,
                  "& .MuiListItemIcon-root, & .MuiListItemText-primary, & .MuiListItemText-secondary, & .MuiSvgIcon-root":
                    {
                      color: "inherit",
                    },
                  "&:hover, &.Mui-focusVisible": {
                    backgroundColor: theme.palette.primary.dark,
                    color: theme.palette.white.main,
                  },
                  "@media (forced-colors: active)": {
                    forcedColorAdjust: "none",
                    backgroundColor: "Highlight",
                    color: "HighlightText",
                    "& .MuiListItemIcon-root, & .MuiListItemText-primary, & .MuiListItemText-secondary, & .MuiSvgIcon-root":
                      {
                        color: "HighlightText",
                      },
                  },
                },
              }),
            },
          },
          MuiListItemButton: {
            styleOverrides: {
              root: ({ theme }) => ({
                "&:focus-visible": {
                  outline: "3px solid",
                  outlineColor: theme.palette.black.main,
                  outlineOffset: "-2px",
                },
                "&.Mui-selected": {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.white.main,
                  "& .MuiListItemIcon-root, & .MuiListItemText-primary, & .MuiListItemText-secondary, & .MuiSvgIcon-root":
                    {
                      color: "inherit",
                    },
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                    color: theme.palette.white.main,
                  },
                  "@media (forced-colors: active)": {
                    forcedColorAdjust: "none",
                    backgroundColor: "Highlight",
                    color: "HighlightText",
                    "& .MuiListItemIcon-root, & .MuiListItemText-primary, & .MuiListItemText-secondary, & .MuiSvgIcon-root":
                      {
                        color: "HighlightText",
                      },
                  },
                },
              }),
            },
          },
          MuiAutocomplete: {
            defaultProps: {
              // Per the WAI-ARIA APG combobox pattern, the popup indicator
              // (and popup/its descendants) are intentionally excluded from
              // the page Tab sequence as they're reachable via dedicated
              // keys while focus stays on the combobox itself, not via a
              // separate Tab stop. MUI's clear ("x") button follows the
              // same design (tabIndex={-1}); Escape
              // is its documented keyboard equivalent to clicking it.
              clearOnEscape: true,
            },
            styleOverrides: {
              option: ({ theme }) => ({
                // MUI's own default styles (see Autocomplete.js) declare
                // "&[aria-selected=true]" backgroundColor rules (plain, and
                // combined with ".Mui-focused"/".Mui-focusVisible") at the
                // same specificity as these and win the tiebreak, so
                // !important is needed on each to reliably beat them -
                // confirmed in a real browser, since MUI's rule wins even
                // with a matching selector structure otherwise. This matters
                // most for "&.Mui-focused": the dropdown auto-highlights the
                // selected option as focused when it opens, so that's the
                // default state a keyboard/mouse user sees.
                '&[aria-selected="true"]': {
                  backgroundColor: `${theme.palette.primary.main} !important`,
                  color: theme.palette.white.main,
                  "@media (forced-colors: active)": {
                    forcedColorAdjust: "none",
                    backgroundColor: "Highlight !important",
                    color: "HighlightText",
                  },
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                    color: theme.palette.white.main,
                  },
                  "&.Mui-focused": {
                    backgroundColor: `${theme.palette.primary.dark} !important`,
                    color: theme.palette.white.main,
                  },
                  "&.Mui-focusVisible": {
                    backgroundColor: `${theme.palette.primary.dark} !important`,
                    color: theme.palette.white.main,
                  },
                },
                "&.Mui-focused, &.Mui-focusVisible": {
                  outline: "3px solid",
                  outlineColor: theme.palette.black.main,
                  outlineOffset: "-2px",
                },
              }),
            },
          },
          MuiToggleButton: {
            styleOverrides: {
              root: ({ theme }) => ({
                "&.Mui-selected": {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.white.main,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                    color: theme.palette.white.main,
                  },
                  "@media (forced-colors: active)": {
                    forcedColorAdjust: "none",
                    backgroundColor: "Highlight",
                    color: "HighlightText",
                  },
                },
              }),
            },
          },
          // a11y: forced colors flattens the selected day's brand fill to Canvas,
          // so it becomes indistinguishable from the other days. Opt out and use
          // the system Highlight pair, and give today its own system-colour border.
          MuiPickerDay: {
            styleOverrides: {
              root: {
                "@media (forced-colors: active)": {
                  "&.Mui-selected": {
                    forcedColorAdjust: "none",
                    backgroundColor: "Highlight",
                    color: "HighlightText",
                  },
                  "&.MuiPickerDay-today": {
                    border: "2px solid CanvasText",
                  },
                },
              },
            },
          },
          // a11y: thicken the fieldset so the ring cuts around the floating label,
          // which an `outline` on the root would run through. Sibling selector
          // rather than `:has()`, which jsdom cannot parse.
          MuiOutlinedInput: {
            styleOverrides: {
              root: ({ theme }) => ({
                "& :focus-visible ~ .MuiOutlinedInput-notchedOutline": {
                  borderWidth: 3,
                  borderColor: theme.palette.black.main,
                  "@media (forced-colors: active)": { borderWidth: 4 },
                },
              }),
            },
          },
          MuiLink: {
            styleOverrides: {
              root: ({ theme }) => ({
                "&:focus-visible": {
                  outline: `3px solid ${theme.palette.black.main}`,
                  outlineOffset: 2,
                },
              }),
            },
          },
          MuiButton: {
            styleOverrides: {
              root: ({ theme }) => ({
                "&:focus-visible": {
                  outline: `3px solid ${theme.palette.black.main}`,
                  "@media (forced-colors: active)": { outlineWidth: 4 },
                },
              }),
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: ({ theme }) => ({
                "&:focus-visible": {
                  outline: `3px solid ${theme.palette.black.main}`,
                  "@media (forced-colors: active)": { outlineWidth: 4 },
                },
              }),
            },
          },
          MuiTab: {
            styleOverrides: {
              root: ({ theme }) => ({
                "&:focus-visible": {
                  outline: `3px solid ${theme.palette.black.main}`,
                  outlineOffset: -3,
                  "@media (forced-colors: active)": { outlineWidth: 4 },
                },
              }),
            },
          },
          MuiFilledInput: {
            styleOverrides: {
              root: ({ theme }) => ({
                "&:focus-within": {
                  outline: `3px solid ${theme.palette.black.main}`,
                  outlineOffset: 1,
                  "@media (forced-colors: active)": { outlineWidth: 4 },
                },
              }),
            },
          },
        },
      }),
    [props.colors, props.direction],
  );

  const themeTree = (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline enableColorScheme>{props.children}</CssBaseline>
    </ThemeProvider>
  );

  // RTL swaps the default MUI cache for one that flips emitted CSS. LTR keeps the existing
  // injectFirst path untouched so English rendering is unchanged.
  if (props.direction === "rtl") {
    return <CacheProvider value={rtlCache}>{themeTree}</CacheProvider>;
  }

  return <StyledEngineProvider injectFirst>{themeTree}</StyledEngineProvider>;
}
